import { REVALIDATE_TIME } from "@/constants/cache"
import { POST_PATH } from "@/constants/path-api"
import httpClient from "@/lib/http-client"
import { safeReadLocale } from "@/lib/safe-locale"
import { message } from "antd"
import { AxiosRequestConfig } from "axios"
import { HttpClient } from "../app/api/HttpClient"
import {
  BANNER_FETCH_PAGE_SIZE,
  BANNER_PAGE_NEWS_DETAIL,
  BANNER_VALID_POSITIONS,
  FEATURED_POSTS_CATEGORY_ID_TAMDAMEDIA,
  RELATED_POSTS_DEFAULT_LOCALE,
  RELATED_POSTS_PAGE_SIZE,
  RELATED_POSTS_SORT,
  SIDEBAR_POSTS_DEFAULT_LOCALE,
  SIDEBAR_POSTS_DEFAULT_PAGE_SIZE,
  SIDEBAR_POSTS_SORT,
} from "../constants/constant"
import {
  API_PREFIX_POST_PATH,
  API_PREFIX_TAG_PATH,
  API_PREFIX_TAMDA_MEDIA_SETTING_PATH,
} from "../constants/prefix-path-api"
import { DataImage, PostData } from "../lib/config/types"
import { FilterSearchParams } from "../lib/external-components"
import {
  BannerData,
  PostData as PostDataFromTypes,
  PostLocalizationPayload,
  SidebarPost,
  SocialShareSettings,
} from "../types/types"
import { buildUrlWithQuery } from "../utils/query-string-api-helper"
import { fetchBanners } from "./banner"

type PostDataForServices = PostDataFromTypes

const POSTS_LIST_POPULATE = {
  image: {
    fields: ["url", "width", "height", "alternativeText", "formats"],
  },
  cover: {
    fields: ["url", "width", "height", "alternativeText", "formats"],
  },
  categories: {
    fields: ["id", "slug", "name"],
  },
  websites: {
    fields: ["domain"],
  },
} as const

// `locale` cần cho việc build link đúng theo locale của chính bài (slug thuộc
// locale nào thì URL phải theo locale đó). Strapi i18n vẫn trả field này kèm
// fields whitelist, nhưng khai báo tường minh cho chắc.
const POSTS_LIST_FIELDS = [
  "id",
  "title",
  "name",
  "slug",
  "description",
  "publishedAt",
  "createdAt",
  "views",
  "locale",
] as const

type PopulateObject = {
  image?: { fields?: readonly string[]; populate?: string | "*" }
  cover?: { fields?: readonly string[]; populate?: string | "*" }
  categories?: { fields: readonly string[] }
  websites?: { fields: readonly string[] }
}

interface PostsListParams extends Omit<FilterSearchParams, "populate"> {
  populate: PopulateObject
  fields?: readonly string[]
}

export const fetchPostAll = async (filters = {}, locale?: string) => {
  const params: FilterSearchParams = {
    pagination: {
      pageSize: "100",
    },
    // populate: "*",
    filters: {},
    sort: "createdAt:desc",
    publicationState: "live",
    locale: safeReadLocale(locale),
  }
  return await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
}

export const fetchPostByCategories = async (
  categoriesId: string[],
  options: { page?: string; pageSize?: string; locale?: string; sort?: string; siteId?: string; domain?: string } = {},
) => {
  const { page = "1", pageSize = "10", locale, sort = "publishedAt:desc", siteId, domain } = options
  const filters: any = {
    // Floor: a public post must be assigned to a website. Without this,
    // when `siteId` fails to resolve no website filter is applied and every
    // post — including orphans with no domain — leaks onto the site.
    websites: {
      domain: {
        $notNull: true,
      },
    },
  }

  // Empty list = "mọi category" (block chưa chọn tab nào), không thêm filter.
  if (categoriesId.length > 0) {
    filters.categories = {
      id: {
        $in: categoriesId,
      },
    }
  }

  if (domain) {
    // An explicit domain (e.g. picked in the page builder) is the narrowest
    // filter available, so it wins over the siteId bucketing below.
    filters.websites.domain.$eq = domain
  } else if (siteId === "tamdamedia.eu") {
    filters.websites.domain.$eq = "tamdamedia.eu"
  } else if (siteId === "tamdagroup.eu") {
    // Grouped bucket = every non-media domain.
    filters.websites.domain.$ne = "tamdamedia.eu"
  }

  const params: PostsListParams = {
    populate: POSTS_LIST_POPULATE,
    fields: POSTS_LIST_FIELDS,
    sort: sort,
    publicationState: "live",
    filters: filters,
    pagination: {
      page,
      pageSize,
    },
    locale: locale || "vi",
  }

  try {
    // Use `buildUrlWithQuery` so we don't rely on `http-client.ts` query serialization
    // (Strapi supports nested query params and arrays).

    const response = await httpClient.get<{ data: PostData[]; meta: any }>(buildUrlWithQuery(POST_PATH, params), null, {
      next: {
        revalidate: REVALIDATE_TIME,
        tags: [
          `posts_by_categories_${categoriesId.length > 0 ? categoriesId.join("_") : "all"}_${domain || siteId || "all"}_${locale || "vi"}_${page}_${pageSize}`,
        ],
      },
    })

    // The UI layer uses `PostData` from `src/types/types.ts` where `attributes.name` and `attributes.excerpt`
    // are required. Strapi responses are typed as `PostData` from `src/lib/config/types.ts` where these
    // fields are optional, so we normalize them here to keep the types compatible.
    const data: PostDataForServices[] = (response.data || []).map(
      (post): PostDataForServices => ({
        ...(post as unknown as PostDataForServices),
        attributes: {
          ...(post.attributes as unknown as PostDataForServices["attributes"]),
          name: post.attributes?.name ?? "",
          excerpt: post.attributes?.excerpt ?? "",
        },
      }),
    )

    return {
      data,
      meta: response.meta || { pagination: { page: 1, pageSize: 10, total: 0, pageCount: 0 } },
    }
  } catch (error) {
    console.error("Error fetching posts by categories:", error)
    throw error
  }
}

export const fetchPosts = async (searchTerms: { [key: string]: string } = {}, locale?: string) => {
  const { siteId, ...otherSearchTerms } = searchTerms
  const params: PostsListParams = {
    populate: POSTS_LIST_POPULATE,
    fields: POSTS_LIST_FIELDS,
    sort: "createdAt:desc",
    publicationState: "preview",
    filters: {},
    pagination: {
      page: "1",
      pageSize: "20",
    },
    locale: safeReadLocale(locale),
  }
  // Floor: never surface posts with no website (orphans) on a public site.
  params.filters.websites = { domain: { $notNull: true } }
  if (siteId === "tamdamedia.eu") {
    params.filters.websites.domain.$eq = "tamdamedia.eu"
  } else if (siteId === "tamdagroup.eu") {
    params.filters.websites.domain.$ne = "tamdamedia.eu"
  }

  const paginationKeys = ["page", "pageSize", "start", "limit"]

  Object.keys(otherSearchTerms).forEach(key => {
    const value = otherSearchTerms[key]

    if (key === "publicationState") {
      const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
      params.publicationState = convertParamStatusPost

      if (value === "draft") {
        params.filters.publishedAt = { $null: true }
      } else {
        delete params.filters.publishedAt
      }
    } else if (key === "locale") {
      params.locale = value
    } else if (paginationKeys.includes(key)) {
      if (value) {
        params.pagination = {
          ...params.pagination,
          [key]: value.toString(),
        }
      }
    } else {
      if (key === "category" || key === "categories") {
        params.filters.categories = { id: { $eq: value } }
      } else if (value && key !== "total" && key !== "current") {
        params.filters[key] = { $containsi: value }
      }
    }
  })

  try {
    const response = await httpClient.get<{ data: PostDataForServices[]; meta: unknown }>(
      buildUrlWithQuery(POST_PATH, params),
    )
    if (!response) {
      return {
        data: [],
        meta: undefined,
      }
    }
    const modifiedResponse =
      response.data?.map((post: PostDataForServices) => ({
        ...post,
        status: post.attributes.publishedAt ? "published" : "draft",
        publishedAt: post.attributes.publishedAt,
      })) || []

    return {
      data: modifiedResponse,
      meta: response.meta,
    }
  } catch (error) {
    message.error("Không thể lấy bài viết. Vui lòng thử lại.")
  }
}

export const searchPosts = async (
  query: string,
  locale?: string,
  pagination = { page: 1, pageSize: 20 },
  siteId?: string | null,
) => {
  const params: PostsListParams = {
    populate: POSTS_LIST_POPULATE,
    fields: POSTS_LIST_FIELDS,
    sort: "createdAt:desc",
    publicationState: "live",
    filters: {
      title: { $containsi: query },
    },
    pagination: {
      page: pagination.page.toString(),
      pageSize: pagination.pageSize.toString(),
    },
    locale: locale || "vi",
  }
  // Floor: never surface posts with no website (orphans) on a public site.
  params.filters.websites = { domain: { $notNull: true } }
  if (siteId === "tamdamedia.eu") {
    params.filters.websites.domain.$eq = "tamdamedia.eu"
  } else if (siteId === "tamdagroup.eu") {
    params.filters.websites.domain.$ne = "tamdamedia.eu"
  }
  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
    return {
      data: response.data || [],
      meta: response.meta || { pagination: { page: 1, pageSize: 20, total: 0, pageCount: 0 } },
    }
  } catch (error) {
    console.error("Error searching posts:", error)
    return {
      data: [],
      meta: { pagination: { page: 1, pageSize: 20, total: 0, pageCount: 0 } },
    }
  }
}

export const fetchPostId = async (id: number, locale?: string) => {
  try {
    const localeParam = safeReadLocale(locale)
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}/${id}?populate=*&locale=${localeParam}`)
    return response.data
  } catch (error) {
    console.error("Error fetching post by ID:", error)
    throw error
  }
}

export const deletePost = async (id: number) => {
  return await HttpClient.delete(`${API_PREFIX_POST_PATH}/${id}`)
}

export const updatePost = async (id: number, data: Record<string, unknown>) => {
  return await HttpClient.put(`${API_PREFIX_POST_PATH}/${id}`, data)
}

export const addPost = async (data: Record<string, unknown>) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}`, data)
}

export const checkSlug = async (slug: string) => {
  const params: AxiosRequestConfig["params"] = {
    filters: {
      slug: { $eq: slug },
    },
    publicationState: "preview",
  }
  return await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
}

export const fetchTag = async () => {
  try {
    const response = await HttpClient.get(`${API_PREFIX_TAG_PATH}?populate=*&pagination[pageSize]=100`)
    return response.data
  } catch (error) {
    console.error("Error fetching tags:", error)
    throw error
  }
}

type PostAttributesWithCover = PostData["attributes"] & {
  cover?: {
    data?: DataImage | null
  }
}

export const fetchRelatedPosts = async (
  excludePostId?: number | string,
  locale?: string,
  domain?: string,
  categories?: Array<{ id: number }>,
  options?: { pageSize?: string; sort?: string },
): Promise<PostData[]> => {
  if (!categories || categories.length === 0) {
    return []
  }
  try {
    const pageSize = options?.pageSize || RELATED_POSTS_PAGE_SIZE
    const sort = options?.sort || RELATED_POSTS_SORT
    const defaultLocale = locale || RELATED_POSTS_DEFAULT_LOCALE

    // Match prod ("Có thể bạn cũng thích"): related = bài CÙNG danh mục với bài
    // đang đọc, sắp theo bài mới nhất (`publishedAt:desc`). Trước đây hàm này bỏ
    // qua danh mục và lấy theo `views:desc`, nên hay trả về bài cũ (vd 2025) chỉ
    // vì lượt xem cao.
    const categoryIds = categories?.map(cat => cat.id).filter(Boolean) ?? []

    const params: PostsListParams = {
      populate: POSTS_LIST_POPULATE,
      fields: POSTS_LIST_FIELDS,
      sort: sort,
      publicationState: "live",
      filters: {},
      pagination: {
        pageSize: pageSize,
      },
      locale: defaultLocale,
    }

    // Floor: related posts must belong to a website; when a concrete `domain`
    // is known, narrow to it. Prevents orphan posts leaking as "related".
    params.filters = {
      websites: {
        domain: domain ? { $eq: domain } : { $notNull: true },
      },
    }

    // Cùng danh mục với bài hiện tại (như prod). Nếu bài không có danh mục thì
    // giữ nguyên fallback theo domain để vẫn hiển thị bài mới nhất của site.
    if (categoryIds.length > 0) {
      params.filters.categories = { id: { $in: categoryIds } }
    }

    const response = await HttpClient.get(API_PREFIX_POST_PATH, {
      params,
      revalidate: REVALIDATE_TIME,
      tags: [`related_posts_${domain ?? "all"}_${defaultLocale}_${categoryIds.join("_") || "all"}`],
    })

    if (response?.data) {
      const posts = response.data.filter((p: PostData) => (excludePostId ? p.id !== excludePostId : true)).slice(0, 10)
      return posts
    }
    return []
  } catch (error) {
    console.error("Failed to fetch related posts:", error)
    return []
  }
}

/**
 * Fetch sidebar posts for post detail page
 * @param excludePostId - Post ID to exclude from results
 * @param categories - Array of category objects with id
 * @param locale - Locale for posts
 * @param pageSize - Page size for pagination
 * @returns Array of sidebar posts
 */
export const fetchSidebarPosts = async (
  excludePostId?: number | string,
  categories?: Array<{ id: number }>,
  locale?: string,
  pageSize?: string,
  siteId?: string,
): Promise<SidebarPost[]> => {
  try {
    // tamdamedia: "Tin nổi bật" = danh mục Nổi bật CỐ ĐỊNH (khớp prod, id 67),
    // giống nhau trên mọi bài viết — một danh sách curated toàn site.
    // tamdagroup: giữ logic động — bài CÙNG chuyên mục với bài đang đọc.
    const categoryIds =
      siteId === "tamdamedia.eu"
        ? [FEATURED_POSTS_CATEGORY_ID_TAMDAMEDIA]
        : categories?.map(cat => cat.id?.toString()).filter(Boolean) || []

    if (categoryIds.length === 0) {
      return []
    }

    const defaultPageSize = pageSize || SIDEBAR_POSTS_DEFAULT_PAGE_SIZE
    const defaultLocale = locale || SIDEBAR_POSTS_DEFAULT_LOCALE

    const response = await fetchPostByCategories(categoryIds, {
      page: "1",
      pageSize: defaultPageSize,
      locale: defaultLocale,
      sort: SIDEBAR_POSTS_SORT,
      siteId,
    })

    if (response?.data) {
      const articles = (response.data as PostDataForServices[])
        .filter(p => (excludePostId ? p.id !== excludePostId : true))
        .map(p => ({
          id: p.id?.toString() || "",
          title: p.attributes?.title || p.attributes?.name || "",
          slug: p.attributes?.slug || "",
          summary: p.attributes?.description || "",
          imageUrl:
            p.attributes?.image?.data?.attributes?.formats?.thumbnail?.url ??
            p.attributes?.image?.data?.attributes?.url ??
            p.attributes?.cover?.data?.attributes?.formats?.thumbnail?.url ??
            p.attributes?.cover?.data?.attributes?.url ??
            undefined,
          publishedAt: p.attributes?.publishedAt || p.attributes?.createdAt || "",
          categoryId: p.attributes?.categories?.data?.[0]?.id?.toString() || "",
          locale: p.attributes?.locale,
        }))
      return articles
    }
    return []
  } catch (error) {
    console.error("Failed to fetch sidebar posts:", error)
    return []
  }
}

export const fetchBannerData = async (): Promise<BannerData[]> => {
  try {
    // Filter by page server-side (like useBannerCategory does for CATEGORY) —
    // pageSize is small and sorted by createdAt:desc, so filtering NEWS_DETAIL
    // client-side after the fetch silently drops it whenever it isn't among
    // the N most recently created banners across every page type.
    const bannerRes = await fetchBanners({
      publicationState: "live",
      pageType: BANNER_PAGE_NEWS_DETAIL,
      pageSize: BANNER_FETCH_PAGE_SIZE,
      // Prod (AdsBanner) không sort và luôn lấy banners[0] → Strapi mặc định
      // trả theo thứ tự tăng dần (cũ nhất trước). Ta sort asc + lấy 1 banner
      // đầu để khớp đúng banner prod hiển thị.
      sort: "createdAt:asc",
    })

    if (bannerRes?.data) {
      const matched = bannerRes.data.filter(b => {
        const position = (b.attributes?.position || "").toLowerCase().trim()
        return BANNER_VALID_POSITIONS.includes(position)
      })
      return matched.slice(0, 1)
    }
    return []
  } catch (error) {
    console.error("Failed to fetch banner:", error)
    return []
  }
}

export const fetchSettings = async (): Promise<SocialShareSettings> => {
  try {
    const response = await HttpClient.get(API_PREFIX_TAMDA_MEDIA_SETTING_PATH, {
      params: {
        fields: ["facebook", "twitter"],
      },
      revalidate: REVALIDATE_TIME,
    })

    if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
      const settings = response.data[0]
      return {
        facebook: settings.attributes?.facebook || "",
        twitter: settings.attributes?.twitter || "",
      }
    }
    if (response?.data?.attributes) {
      return {
        facebook: response.data.attributes?.facebook || "",
        twitter: response.data.attributes?.twitter || "",
      }
    }
    if (response?.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
      const settings = response.data.data[0]
      return {
        facebook: settings.attributes?.facebook || "",
        twitter: settings.attributes?.twitter || "",
      }
    }
    return { facebook: "", twitter: "" }
  } catch (error) {
    console.error("Failed to fetch settings:", error)
    return { facebook: "", twitter: "" }
  }
}

export const createPostLocalization = async (id: number, data: PostLocalizationPayload) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/localizations`, data)
}
